import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  QueryList,
  Renderer2,
  signal,
  ViewChild,
  ViewChildren
} from '@angular/core'
import {filter, Subscription} from 'rxjs'
import {environment} from '../../environments/environment'
import {Dimensions, Overlay, OverlayImage, OverlayItem, OverlayMix, OverlayText} from '../application/types'
import {WINDOW} from '../application/window.provider'
import {ConfigService, NewConfigEvent} from '../services/config.service'
import {NgClass} from '@angular/common'

export class OverlayItemHolder {
  element: HTMLDivElement

  constructor(ref: ElementRef<HTMLDivElement>) {
    this.element = ref.nativeElement
  }

  get image(): HTMLImageElement | null {
    const index = 0
    return this.element.childNodes.item(index).nodeName !== '#comment' ?
      this.element.childNodes.item(index) as HTMLImageElement : null
  }

  get title(): HTMLSpanElement | null {
    const index = this.element.childNodes.length === 4 ? 2 : 1
    return this.element.childNodes.item(index).nodeName !== '#comment' ?
      this.element.childNodes.item(index) as HTMLSpanElement :
      null
  }
}

@Component({
  selector: 'aku-overlay',
  templateUrl: './overlay.component.html',
  imports: [
    NgClass
  ],
  styleUrls: ['./overlay.component.scss']
})
export class OverlayComponent implements AfterViewInit, OnDestroy {

  protected readonly environment = environment

  /**
   * Overlay to be displayed. It can change and it will need to be re-rendered
   * Set initial overlay to avoid breaking errors
   */
  public currentOverlay = signal<Overlay>(new Overlay([], ''))
  /**
   * Div used to create a fading animation
   */
  @ViewChild('overlayFader') public overlayFader!: ElementRef<HTMLDivElement>
  /**
   * HTML elements that represent every OverlayItem appended to the DOM. They
   * are linked to an OverlayItem via ID attribute.
   */
  @ViewChildren('overlayItem') public overlayItems!: QueryList<ElementRef<HTMLDivElement>>

  protected configService = inject(ConfigService)
  /**
   * Width of the Video Player (parent component).
   * It will be used to calculate how bif the overlay items should look later.
   */
  private videoPlayerWidth: number = 0

  private window: Window = inject(WINDOW)

  private readonly elementRef = inject(ElementRef)

  private readonly renderer = inject(Renderer2)
  // Subscriptions to remove and not leak memory
  private readonly subs$: Subscription[] = []


  public get overlayItemHolders(): OverlayItemHolder[] {
    return this.overlayItems
      .map(oI => new OverlayItemHolder(oI))
  }

  public ngOnDestroy() {
    this.subs$.forEach(s => s.unsubscribe())
  }

  public ngAfterViewInit() {
    this.overlayItems.changes.subscribe(() => this.renderOverlay())

    this.subs$.push(
      this.configService.actions$
        .subscribe(actions => {
          actions.forEach(action => {
            switch (action) {
              case 'setup_overlay':
                // Reset current overlay to always trigger a new render.
                // There are some problem when using hor-menu if not.
                this.currentOverlay.set(this.configService.activeOverlay)
                break
              case 'show_overlay':
                // Remove previous "hidden" class and add "visible", which will
                // make it visible (dah) and also animating the opacity
                this.renderer.removeClass(this.elementRef.nativeElement, 'hidden')
                this.renderer.addClass(this.elementRef.nativeElement, 'visible')
                break
              case 'hide_overlay':
                // Add "hidden"", which will make it hidden (dah, again), but
                // also maintaining the opacity to 0, so when going back to
                // "visible" it can do the fade-in animation.
                this.renderer.addClass(this.elementRef.nativeElement, 'hidden')
                break
            }
          })
        })
    )

    // Subscribe to Video Player resizes to re-calculate Overlay items size
    this.subs$.push(
      this.configService.videoPlayerDimensions$
        .pipe(filter(value => value.width > 0 && value.height > 0))
        .subscribe(value => {
          // Calculate the new item size with the new Video Player dimensions
          this.videoPlayerWidth = value.width

          // Resize all elements in the overlay
          this.resizeCurrentOverlay()
        })
    )
  }

  /**
   * It renders the "currentOverlay", adding functionality to all OverlayItems
   * @private
   */
  private renderOverlay() {
    this.overlayItemHolders.forEach((holder) => {
      // Item associated to HTML image element
      // We can safely use "!" because at this point, all of these exist!
      const item = this.currentOverlay().items
        .find(i => i.id === holder.element.id)!

      const span = holder.title
      if (span) {
        // Resize element once is loaded
        this.resizeOverlayItem(item, holder)

        // Add onClick functionality to span element
        this.addClickFunctionality(holder, span, item)
      }

      // Add onLoad functionality to the image element
      const img = holder.image
      if (img) {
        img.onload = () => {
          // Set OverlayItem dimensions from the loaded <img>
          if (item instanceof OverlayImage) {
            item.dimensions = new Dimensions(img.width, img.height)
          } else if (item instanceof OverlayMix) {
            item.image.dimensions = new Dimensions(img.width, img.height)
          }

          // Resize element once is loaded
          this.resizeOverlayItem(item, holder)
        }
        // Sometimes onload will not be triggered if overlay was open when it
        // changed. For those cases, we call resize here too.
        // It will only work on those specific cases, otherwise, because item
        // has no dimensions, it will do nothing.
        this.resizeOverlayItem(item, holder)

        // Add onClick functionality to img element
        this.addClickFunctionality(holder, img, item)
      }
    })
  }

  private addClickFunctionality(holder: OverlayItemHolder, element: HTMLElement, item: OverlayItem) {
    if (item.videoId) {
      element.onclick = this.createOnClickVideo(holder, element, item.videoId)
    } else if (item.section) {
      element.onclick = this.createOnClickSection(element, item.section)
    } else if (item.link) {
      element.onclick = this.createOnClickLink(item.link)
    }
  }

  private createOnClickVideo(holder: OverlayItemHolder, element: HTMLElement, videoId: string): () => void {
    return (() => {
      // Disable click instantly to avoid double-clicking!
      element.onclick = null

      // Do a click animation on POI
      this.renderer.addClass(holder.element, 'click-animation')

      // Fade-out overlay, close it and send config event after animation ends
      // 1. Start black fader animation
      this.renderer.addClass(this.overlayFader.nativeElement, 'black')
      this.renderer.addClass(this.overlayFader.nativeElement, 'active')
      setTimeout(() => {
        // 2. Send ConfigEvent
        this.configService.sendEvent(NewConfigEvent.OverlayClick(videoId))

        // 3. Remove animation classes for fader
        this.renderer.removeClass(this.overlayFader.nativeElement, 'active')
        this.renderer.removeClass(this.overlayFader.nativeElement, 'black')
      }, 300)
    })
  }

  private createOnClickSection(element: HTMLElement, section: string): () => void {
    return (() => {
      // Disable click instantly to avoid double-clicking!
      element.onclick = null

      // Flash-out overlay, close it and send config event after animation ends
      this.renderer.addClass(this.overlayFader.nativeElement, 'active')
      // Opacity going up in 800ms
      setTimeout(() => {
        // Send ConfigEvent
        this.configService.sendEvent(NewConfigEvent.SectionClick(section))

        // Remove animation class
        this.renderer.addClass(this.overlayFader.nativeElement, 'inactive')
        this.renderer.removeClass(this.overlayFader.nativeElement, 'active')
      }, 800)
      // Opacity going down again in 800ms
      setTimeout(() => {
        // Remove animation class
        this.renderer.removeClass(this.overlayFader.nativeElement, 'inactive')
      }, 1600)
    })
  }

  private createOnClickLink(link: string): () => void {
    return (() => {
      window.open(link, '_blank')
    })
  }

  /**
   * Resizes all the overlay items contained in the "currentOverlay"
   * @private
   */
  private resizeCurrentOverlay() {
    this.currentOverlay()?.items.forEach((item: OverlayItem) => {
      const holder = this.overlayItemHolders
        .find(h => h.element.id === item.id)
      if (holder) {
        this.resizeOverlayItem(item, holder)
      }
    })
  }

  /**
   * We set a new size according to new Video Player's width, taking every item
   * proportion and calculating their new size.
   * @param item OverlayItem to resize
   * @param holder Elements group associated to Overlay Item
   * @private
   */
  private resizeOverlayItem(item: OverlayItem, holder: OverlayItemHolder) {
    if (item instanceof OverlayMix) {
      // // When item is a "mix", there is a divider, so the item that
      // // truly resizes is this div, image just adapts to this div (width: 100%)
      // this.renderer.setStyle(holder.element, 'width', `${item.image.proportion * 100}%`)
      // this.renderer.setStyle(holder.image, 'width', `100%`)

      this.resizeOverlayItem(item.image, holder)
      this.resizeOverlayItem(item.title, holder)
      return
    }

    // Set new dimensions to image element.
    if (holder.image && item instanceof OverlayImage && item.dimensions) {
      // When item is a "mix", there is a divider, so the item that
      // truly resizes is this div, image just adapts to this div (width: 100%)
      this.renderer.setStyle(holder.element, 'width', `${item.proportion * 100}%`)
      this.renderer.setStyle(holder.image, 'width', `100%`)
      // // We only set width, since <img> will keep its aspect ratio accordingly
      // this.renderer.setStyle(holder.image, 'width', `${item.proportion * 100}%`)
    }

    // Set new dimensions to title element
    if (holder.title && item instanceof OverlayText) {
      const fontSize = this.videoPlayerWidth * item.proportion
      this.renderer.setStyle(holder.title, 'fontSize',
        `${fontSize}px`)
      this.renderer.setStyle(holder.title, 'lineHeight',
        `${fontSize * 1.5}px`)
    }
  }
}
